import { initializeApp } from 'firebase/app';
import { connectAuthEmulator, getAuth } from 'firebase/auth';
import { getAnalytics, isSupported } from 'firebase/analytics';

const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
    measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

const firebaseApp = initializeApp(firebaseConfig);

const auth = getAuth();

// Point the client at a local Firebase Auth emulator when one is configured, so
// a regression pass can sign in as each role without anyone typing credentials.
// Unset by default: a normal build authenticates against real Firebase exactly
// as before, and no auth-bypass branch exists in the application as a result.
//
// The port is not Firebase's 9099 default. It follows this checkout's
// PORT_OFFSET (9099 + offset), because two projects both sitting on 9099 do not
// fail loudly: the second one authenticates against the first one's emulator,
// sign-in succeeds, and the server then finds no profile for a UID belonging to
// another project's fixtures. scripts/development/start-container derives this.
//
// The host must be loopback. A stray or injected value would otherwise send real
// users' credentials to an arbitrary host, so this is treated as untrusted input
// even though it is developer-supplied.
const emulatorHost = process.env.REACT_APP_FIREBASE_AUTH_EMULATOR_HOST;
if (emulatorHost) {
    if (/^(127\.0\.0\.1|localhost|\[::1\]):\d+$/.test(emulatorHost)) {
        connectAuthEmulator(auth, `http://${emulatorHost}`, {
            disableWarnings: true,
        });
    } else {
        // Loud rather than silent: a misconfigured host means the pass is
        // authenticating somewhere unintended, which must not look like success.
        throw new Error(
            `REACT_APP_FIREBASE_AUTH_EMULATOR_HOST must be a loopback address, got "${emulatorHost}"`
        );
    }
}

/**
 * Initialize Firebase Analytics after cookie consent is given.
 * Called by TermlyCMP when the user accepts analytics cookies.
 */
let analyticsInstance = null;

function initAnalytics() {
    if (analyticsInstance) return analyticsInstance;
    return isSupported().then((supported) => {
        if (supported) {
            analyticsInstance = getAnalytics(firebaseApp);
        }
        return analyticsInstance;
    });
}

export default {
    firebaseApp,
    auth,
    initAnalytics,
};
